import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, UseFormGetValues, UseFormReset } from 'react-hook-form';

import { useDisclosure, UseDisclosureReturn } from '@chakra-ui/react';

import { format } from 'date-fns/format';
import { urlJoinP } from 'url-join-ts';

import { urlMap } from '@nocowanie/core';

import { RANGE_PICKER } from '../../../consts';
import { GeographyTypeEnum } from '../../../enums';
import { UseAutocompleteDataReturnModel } from '../../../models';
import { SearchEngineModel } from '../../../models/search-engine.model';
import { AutocompleteBaseItemModel } from '../../molecules/autocomplete/autocomplete-base-item.model';

interface UseSearchEngineActions<T> {
    getValues: UseFormGetValues<SearchEngineModel>;
    reset: UseFormReset<SearchEngineModel>;
    onSearchEngineSubmit?: ({
        queryParams,
        searchEngineData,
    }: {
        queryParams: string;
        searchEngineData: SearchEngineModel;
    }) => void;
    baseUrl?: string;
    defaultBaseUrl?: string;
    mobileDrawerDisclosure?: UseDisclosureReturn;
    addFragment?: () => void;
    autocompleteData?: UseAutocompleteDataReturnModel<T>;
    isMapSearch?: boolean;
}

export const useSearchEngineActions = <T extends AutocompleteBaseItemModel>({
    getValues,
    reset,
    addFragment,
    mobileDrawerDisclosure,
    onSearchEngineSubmit,
    baseUrl = urlMap.search,
    defaultBaseUrl = urlMap.search,
    autocompleteData,
    isMapSearch = false,
}: UseSearchEngineActions<T>) => {
    const defaultMobileDisclosure = useDisclosure();
    const { open, onOpen, onClose } = mobileDrawerDisclosure ?? defaultMobileDisclosure;

    const onSubmit: SubmitHandler<SearchEngineModel> = useCallback(
        async data => {
            const {
                location: initialLocation,
                dates,
                guests,
                instantReservation,
                flexDates,
            } = data;
            const transitionDuration = 300;

            const autocompleteLocationsNames =
                autocompleteData && autocompleteData.items && Array.isArray(autocompleteData.items)
                    ? autocompleteData.items.map(item => item.name)
                    : [];

            const firstAutocompleteLocation =
                autocompleteData &&
                autocompleteData.items &&
                Array.isArray(autocompleteData.items) &&
                autocompleteData.items.length
                    ? autocompleteData.items[0].name
                    : '';

            const location =
                !firstAutocompleteLocation || autocompleteLocationsNames.includes(initialLocation)
                    ? initialLocation
                    : firstAutocompleteLocation;

            if (initialLocation !== location) {
                reset({
                    ...data,
                    location,
                });
            }

            const baseParams: Record<string, string> = {
                ...(baseUrl === defaultBaseUrl
                    ? {
                          command: 'search_location',
                      }
                    : undefined),
                q: location,
                'data[od]': dates.checkInDate
                    ? format(new Date(dates.checkInDate), 'yyyy-MM-dd')
                    : '',
                'data[do]': dates.checkOutDate
                    ? format(new Date(dates.checkOutDate), 'yyyy-MM-dd')
                    : '',
                miejsca_dorosli: guests.adultsCount.toString(),
                miejsca_dzieci: guests.childrenCount ? guests.childrenCount.toString() : '0',
                wiek_dzieci: guests.childrenAges ? guests.childrenAges.join(',') : '',
                miejsca: (guests.adultsCount + guests.childrenCount).toString(),
                rezerwacja_online: instantReservation ? '1' : '0',
            };

            const flexDatesValue = flexDates ?? RANGE_PICKER.FLEX_DATES.DEFAULT;
            if (flexDatesValue > 0) {
                baseParams['flex_dates'] = flexDatesValue.toString();
            }

            const queryParams = new URLSearchParams(baseParams).toString();

            onSearchEngineSubmit?.({
                queryParams: queryParams,
                searchEngineData: data,
            });

            onClose();

            if (isMapSearch) {
                setTimeout(() => {
                    reset(getValues());
                    window.location.hash = '';
                }, 0);
                return;
            }

            const queryParamsString =
                autocompleteData?.items?.[0]?.type === GeographyTypeEnum.accommodation
                    ? ''
                    : `?${queryParams}`;

            setTimeout(
                () => {
                    window.location.href = urlJoinP(window.location.origin + baseUrl, [
                        queryParamsString,
                    ]);

                    reset(getValues());
                },
                isNaN(transitionDuration) ? 200 : transitionDuration,
            ); // we retrieve default value of transition from chakra
        },
        [
            autocompleteData,
            baseUrl,
            defaultBaseUrl,
            getValues,
            onClose,
            onSearchEngineSubmit,
            reset,
            isMapSearch,
        ],
    );

    const [isMobileDrawerPending, setIsMobileDrawerPending] = useState<boolean>(false);

    const handleDrawerOpen = useCallback(() => {
        setIsMobileDrawerPending(true);
        requestAnimationFrame(() => {
            addFragment?.();
            onOpen();
        });
    }, [addFragment, onOpen]);

    useEffect(() => {
        if (open) {
            setIsMobileDrawerPending(false);
        }
    }, [open]);

    return {
        handleDrawerOpen,
        isMobileDrawerPending,
        open,
        onClose,
        onOpen,
        onSubmit,
    };
};
